<template>
  <div class="lines-editor">
    <div v-for="row in rows" :key="row.uid" class="article">
      <div class="article-main">
        <CustomTextField :content=row.designation />
        <button type="button" class="button-icon-custom" title="Retirer l'article" @click="removeRow(row)"><span class="fas fa-xmark"></span></button>
      </div>
      <div class="article-values">
        <div class="field"><span class="caption">Quantité</span><CustomTextField :content=row.quantity /></div>
        <div class="field"><span class="caption">Unité</span><CustomSelectField :content=row.unit /></div>
        <div class="field"><span class="caption">Prix unitaire HT (€)</span><CustomTextField :content=row.unitPriceHt /></div>
        <div class="field"><span class="caption">TVA</span><CustomSelectField :content=row.vatRate /></div>
        <div class="field total"><span class="caption">Total HT</span><strong>{{ formatAmount(htOf(row)) }}</strong></div>
      </div>
      <div class="article-catalog">
        <span class="caption">Catalogue</span>
        <CustomSelectField :content=row.articleId />
        <button v-if="canRegister(row)" type="button" class="link-button" title="Ajouter cet article au catalogue, pour suivre son stock d'un achat à l'autre" @click="registerRow(row)">Enregistrer dans le catalogue</button>
      </div>
      <div v-if="hasCreditedLines" class="article-credit">
        <span class="caption">Marchandise rendue</span>
        <CustomSelectField :content=row.creditedLineId />
      </div>
    </div>

    <div class="actions">
      <button type="button" class="link-button" @click="addRow()">+ Ajouter un article</button>
      <button v-if="hasCreditedLines" type="button" class="link-button" @click="addCreditedLines">Reprendre les articles de la facture</button>
    </div>
    <p v-if="rows.length > 0" class="totals">
      HT {{ formatAmount(totals.ht) }} · TVA {{ formatAmount(totals.vat) }} · <strong>TTC {{ formatAmount(totals.ttc) }}</strong>
    </p>
  </div>
</template>

<script>
import { library } from '@fortawesome/fontawesome-svg-core';
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import CustomTextField from "@/components/forms/customs/customTextField/CustomTextField.vue";
import CustomSelectField from "@/components/forms/customs/customSelectField/CustomSelectField.vue";
import {NotificationService} from "@vues/v-common";
import {ArticleController} from "@/purchase/controllers/ArticleController";
import {formatAmount, idOf, newLineId, round2, unitSelectValues, vatRateSelectValues} from "@/purchase/utils";
library.add(faXmark);

const NO_CREDITED_LINE = '';
const NO_ARTICLE = '';

// Articles d'un achat (facultatifs) : désignation, quantité, unité, prix et TVA. Avec des articles, le montant de l'achat en découle.
// Une ligne peut se rattacher à un article du catalogue (ms-purchase) : l'article préremplit la ligne, et permet de suivre son stock d'un achat à l'autre.
// Sur un avoir, un article peut viser un article de la facture (marchandise rendue au fournisseur, `creditedLines`).
// Méthodes : load(lignes), reset(), getValue() (lignes à envoyer, undefined si la saisie est invalide).
// callback est appelé à chaque changement avec {count, totals, isDirty, lines} : isDirty vaut false tant que les articles sont ceux enregistrés.
export default {
  name: 'PurchaseLinesEditor',
  props: ['callback', 'creditedLines'],
  components: {CustomTextField, CustomSelectField},
  data() {
    return {
      rows: [],
      uidCounter: 0,
      savedKey: '',      // empreinte des articles enregistrés : sert à savoir si la saisie a changé
      articles: [],      // articles du catalogue proposés à chaque ligne
    };
  },
  computed: {
    totals() {
      const htByRate = new Map();
      this.rows.forEach(row => htByRate.set(row.vatRate.value, (htByRate.get(row.vatRate.value) ?? 0) + Math.round(this.htOf(row) * 100)));
      let ht = 0, vat = 0;
      htByRate.forEach((cents, rate) => {
        ht += cents;
        vat += Math.round(cents * Number(rate) / 100);
      });
      return {ht: ht / 100, vat: vat / 100, ttc: (ht + vat) / 100};
    },
    key() {
      return JSON.stringify(this.rows.map(row => [row.id, row.designation.value, row.quantity.value, row.unit.value, row.unitPriceHt.value, row.vatRate.value, row.creditedLineId.value, row.articleId.value]));
    },
    // Articles du catalogue à choisir pour une ligne
    articleValues() {
      return [{label: '—', value: NO_ARTICLE}, ...this.articles.map(article => ({label: article.reference ? `${article.name} (${article.reference})` : article.name, value: String(idOf(article))}))];
    },
    articleKey() {
      return JSON.stringify(this.rows.map(row => [row.uid, row.articleId.value]));
    },
    hasCreditedLines() {
      return (this.creditedLines ?? []).length > 0;
    },
    creditedLineValues() {
      return [{label: '—', value: NO_CREDITED_LINE}, ...(this.creditedLines ?? []).map(line => ({label: line.designation, value: line.id}))];
    },
  },
  watch: {
    key() {
      this.notify();
    },
    creditedLines() {
      this.rows.forEach(row => { row.creditedLineId.selectValues = this.creditedLineValues; });
    },
    articleValues() {
      this.rows.forEach(row => { row.articleId.selectValues = this.articleValues; });
    },
    // Choisir un article remplit la ligne avec ses valeurs habituelles
    articleKey() {
      this.rows.forEach(row => {
        if ((row.articleId.value || NO_ARTICLE) !== row.appliedArticleId) this.applyArticle(row);
      });
    },
  },
  methods: {
    formatAmount,
    toNumber(value) {
      const number = Number(String(value ?? '').replace(',', '.'));
      return isNaN(number) ? 0 : number;
    },
    isBlank(value) {
      return String(value ?? '').trim() === '';
    },
    // Total HT d'un article : celui enregistré tant que ni la quantité ni le prix n'ont changé, sinon quantité x prix
    htOf(row) {
      if (row.saved && row.saved.quantity === this.toNumber(row.quantity.value) && row.saved.unitPriceHt === this.toNumber(row.unitPriceHt.value)) return row.saved.htAmount;
      return round2(this.toNumber(row.quantity.value) * this.toNumber(row.unitPriceHt.value));
    },
    newRow({id, designation = '', quantity = '', unit = 'u', unitPriceHt = '', vatRate = 20, creditedLineId = NO_CREDITED_LINE, articleId = NO_ARTICLE, saved} = {}) {
      this.uidCounter++;
      return {
        uid: this.uidCounter,
        id: id ?? newLineId(),
        saved,
        appliedArticleId: articleId,   // article dont les valeurs ont déjà été reportées sur la ligne
        designation: {value: designation, type: 'text', placeholder: 'Désignation'},
        quantity: {value: quantity, type: 'number', step: 'any', placeholder: '0'},
        unit: {value: unit || 'u', type: 'select', selectValues: unitSelectValues(unit)},
        unitPriceHt: {value: unitPriceHt, type: 'number', step: 'any', placeholder: '0,00'},
        vatRate: {value: vatRate, type: 'select', selectValues: vatRateSelectValues(vatRate)},
        creditedLineId: {value: creditedLineId, type: 'select', selectValues: this.creditedLineValues},
        articleId: {value: articleId, type: 'select', selectValues: this.articleValues},
      };
    },
    addRow(preset) {
      this.rows.push(this.newRow(preset));
    },
    removeRow(row) {
      this.rows.splice(this.rows.indexOf(row), 1);
    },
    // Avoir : un article par article de la facture, la quantité rendue restant à saisir
    addCreditedLines() {
      (this.creditedLines ?? []).forEach(line => this.addRow({
        designation: line.designation, unit: line.unit, unitPriceHt: line.unitPriceHt, vatRate: line.vatRate, creditedLineId: line.id,
        articleId: line.articleId ?? NO_ARTICLE,
      }));
    },
    load(lines = []) {
      this.refreshArticles();
      this.rows = lines.map(line => this.newRow({
        id: line.id, designation: line.designation, quantity: line.quantity, unit: line.unit, unitPriceHt: line.unitPriceHt,
        vatRate: line.vatRate, creditedLineId: line.creditedLineId ?? NO_CREDITED_LINE, articleId: line.articleId ?? NO_ARTICLE,
        saved: {quantity: line.quantity, unitPriceHt: line.unitPriceHt, htAmount: line.htAmount},
      }));
      this.savedKey = this.key;
      this.notify();
    },
    // Le catalogue peut avoir changé depuis la dernière ouverture (un article créé dans l'écran Articles, par exemple)
    async refreshArticles() {
      const articles = await ArticleController.getAll();
      this.articles = articles && !articles.errors ? articles : [];
    },
    articleById(id) {
      return id ? this.articles.find(article => String(idOf(article)) === String(id)) : undefined;
    },
    // Reporte les valeurs habituelles de l'article choisi. Le nom et le prix saisis à la main restent, sauf ceux de l'article précédent.
    applyArticle(row) {
      const previous = this.articleById(row.appliedArticleId);
      const article = this.articleById(row.articleId.value);
      row.appliedArticleId = row.articleId.value || NO_ARTICLE;
      if (!article) return;

      if (this.isBlank(row.designation.value) || (previous && row.designation.value === previous.name)) row.designation.value = article.name;
      if (article.unit) {
        row.unit.selectValues = unitSelectValues(article.unit);
        row.unit.value = article.unit;
      }
      const hasPrice = article.unitPriceHt !== undefined && article.unitPriceHt !== null;
      if (hasPrice && (this.isBlank(row.unitPriceHt.value) || (previous && this.toNumber(row.unitPriceHt.value) === previous.unitPriceHt))) row.unitPriceHt.value = article.unitPriceHt;
      if (article.vatRate !== undefined && article.vatRate !== null) {
        row.vatRate.selectValues = vatRateSelectValues(article.vatRate);
        row.vatRate.value = article.vatRate;
      }
    },
    // Une ligne nommée, qui n'est pas encore rattachée au catalogue, peut y être enregistrée
    canRegister(row) {
      return !row.articleId.value && !this.isBlank(row.designation.value);
    },
    // Ajoute la ligne au catalogue (nom, unité, prix et TVA de la ligne) et la rattache ; un article du même nom est simplement repris
    async registerRow(row) {
      const name = String(row.designation.value ?? '').trim();
      const sameName = (article) => String(article.name).trim().toLowerCase() === name.toLowerCase();
      const existing = this.articles.find(sameName);
      let articleId = existing ? String(idOf(existing)) : undefined;

      if (!existing) {
        const body = {name, unit: row.unit.value, vatRate: this.toNumber(row.vatRate.value)};
        if (!this.isBlank(row.unitPriceHt.value)) body.unitPriceHt = this.toNumber(row.unitPriceHt.value);
        const res = await ArticleController.post(body);
        if (!res || res.errors) return;
        articleId = String(res.id);
        await this.refreshArticles();
      }

      row.appliedArticleId = articleId;
      row.articleId.value = articleId;
      (new NotificationService).addNotification("success", existing ? `« ${name} » est déjà au catalogue : la ligne y est rattachée` : `« ${name} » ajouté au catalogue`);
    },
    reset() {
      this.load([]);
    },
    // Articles tels que saisis (même incomplets) : la ventilation sur les dossiers s'appuie dessus
    liveLines() {
      return this.rows.map(row => ({
        id: row.id, designation: String(row.designation.value ?? '').trim() || 'Article', unit: row.unit.value,
        quantity: this.toNumber(row.quantity.value), htAmount: this.htOf(row),
      }));
    },
    notify() {
      if (this.callback) this.callback({count: this.rows.length, totals: this.totals, isDirty: this.key !== this.savedKey, lines: this.liveLines()});
    },
    fail(input, message) {
      if (input) input.error = true;
      (new NotificationService).addNotification("danger", message);
    },
    // Articles à envoyer ; undefined si un article est incomplet (message déjà affiché)
    getValue() {
      const lines = [];
      for (const row of this.rows) {
        if (this.isBlank(row.designation.value)) return this.fail(row.designation, "La désignation d'un article n'est pas renseignée");
        if (!(this.toNumber(row.quantity.value) > 0)) return this.fail(row.quantity, "La quantité d'un article n'est pas renseignée");
        if (this.isBlank(row.unitPriceHt.value)) return this.fail(row.unitPriceHt, "Le prix d'un article n'est pas renseigné");

        const line = {
          id: row.id,
          designation: String(row.designation.value).trim(),
          quantity: this.toNumber(row.quantity.value),
          unit: row.unit.value,
          unitPriceHt: this.toNumber(row.unitPriceHt.value),
          vatRate: this.toNumber(row.vatRate.value),
        };
        if (row.creditedLineId.value) line.creditedLineId = row.creditedLineId.value;
        if (row.articleId.value) line.articleId = row.articleId.value;
        lines.push(line);
      }
      return lines;
    },
  },
}
</script>

<style lang="stylus" scoped>
@require '~/src/common/style/common.styl';
@require '~/src/purchase/style/section.styl';

.lines-editor
  display flex
  flex-direction column

.article
  margin-bottom 8px
  padding 8px 10px
  border 1px solid #e6eaf2
  border-radius 10px
  background #fbfcfe

.article-main
  display grid
  grid-template-columns 1fr 28px
  align-items center
  gap 10px

.article-values
  display grid
  grid-template-columns 0.8fr 0.8fr 1.2fr 0.9fr 1fr
  align-items end
  gap 10px

.article-credit
  display grid
  grid-template-columns 150px 1fr
  align-items end
  gap 10px
  margin-top 6px

.article-catalog
  display grid
  grid-template-columns 150px 1fr auto
  align-items end
  gap 10px
  margin-top 6px

.caption
  display block
  margin 6px 0 2px 2px
  font-size 0.75em
  font-weight 600
  color #6b7280

.total strong
  display block
  padding-bottom 10px
  text-align right
  white-space nowrap

.actions
  display flex
  flex-wrap wrap
  gap 4px 18px

@media (max-width 600px)
  .article-values
    grid-template-columns 1fr 1fr

  .article-credit
    grid-template-columns 1fr
</style>
